/**
 * Fiyat aralığı filtresi yardımcıları.
 *
 * Mantık iki ayrı bileşende (GeneralFilterPriceSection — üst filtre çubuğu,
 * FilterPriceSection — kenar çubuğu akordiyonu) kopyalanmıştı ve ikisi farklı
 * sonuçlar üretiyordu. Tek kaynağa alındı.
 */

export interface PriceBounds {
  min: number;
  max: number;
}

export interface PriceRange {
  min?: number;
  max?: number;
}

const clamp = (value: number, lo: number, hi: number) =>
  Math.min(Math.max(value, lo), hi);

/**
 * API sınırları ondalıklı geliyor (min: 29.99, max: 2799.99). Kullanıcıya
 * gösterilen ve kırpma için kullanılan aralık, hiçbir ürünü dışarıda
 * bırakmayacak şekilde dışa yuvarlanır: 29.99 → 29, 2799.99 → 2800.
 */
export const getPriceBounds = (min: number, max: number): PriceBounds => ({
  min: Math.max(0, Math.floor(min)),
  max: Math.ceil(max),
});

/**
 * Ham input metnini sayıya çevirir. Boş/geçersiz girdi undefined döner —
 * o uç "serbest" demektir. Ondalık ayracı olarak virgül de kabul edilir.
 * Negatif değerler burada elenmez; kırpma aşamasında alt sınıra çekilir,
 * böylece kullanıcının yazdığı şey sessizce silinmez.
 */
export const parsePriceInput = (raw: string): number | undefined => {
  const trimmed = raw.trim();
  if (!trimmed) return undefined;
  const parsed = Number(trimmed.replace(",", "."));
  return Number.isFinite(parsed) ? parsed : undefined;
};

/**
 * Kullanıcının yazdığı iki değeri geçerli bir aralığa dönüştürür:
 *
 * 1. Her uç bağımsız olarak API sınırlarına kırpılır. (Önceki kod max'ı
 *    kırparken min'in HAM değerini kullanıyordu; min sınır dışıysa max
 *    tamamen alakasız bir değere kayıyordu.)
 * 2. min > max ise değerler takas edilir — kullanıcı ters yazmıştır,
 *    niyeti bellidir. (Önceki kod bunun yerine ±1 ile iki değeri de
 *    bozuyordu: 2000-500 girişi 499-2001'e dönüşüyordu.)
 * 3. min === max serbest bırakılır; tek fiyat araması geçerli bir istektir.
 */
export const normalizePriceRange = (
  rawMin: string,
  rawMax: string,
  bounds: PriceBounds,
): PriceRange => {
  let min = parsePriceInput(rawMin);
  let max = parsePriceInput(rawMax);

  if (min !== undefined) min = clamp(min, bounds.min, bounds.max);
  if (max !== undefined) max = clamp(max, bounds.min, bounds.max);

  if (min !== undefined && max !== undefined && min > max) {
    [min, max] = [max, min];
  }

  return { min, max };
};

/** Redux'a yazmadan önce: aralık gerçekten değişti mi? */
export const isSamePriceRange = (a: PriceRange, b: PriceRange) =>
  a.min === b.min && a.max === b.max;

const formatAmount = (value: number) =>
  value.toLocaleString("tr-TR", { maximumFractionDigits: 2 });

/**
 * Tek uçlu aralıklar için okunabilir etiket. Önceden hem "min: 500" hem
 * "max: 500" durumu "500 ₺" olarak görünüyordu — hangisi olduğu anlaşılmıyordu.
 */
export const formatPriceRangeLabel = (
  range: PriceRange,
  fallback: string,
): string => {
  const { min, max } = range;
  if (min !== undefined && max !== undefined)
    return `${formatAmount(min)} - ${formatAmount(max)} ₺`;
  if (min !== undefined) return `${formatAmount(min)} ₺ ve üzeri`;
  if (max !== undefined) return `${formatAmount(max)} ₺ ve altı`;
  return fallback;
};
